import {
  AlignmentType,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';

// ---------------------------------------------------------------------------
// Data shapes returned by GET /super-admin/validation-data
// ---------------------------------------------------------------------------

export interface ValidationSensorChannel {
  id: number;
  channel: number;
  display_name: string;
  high_alarm: number | null;
  low_alarm: number | null;
  high_enable: boolean;
  low_enable: boolean;
  alarms_disabled: boolean;
}

export interface ValidationSensor {
  name: string;
  id: number;
  serialNo: number;
  type: string;
  firmware: string | null;
  created_at: string;
  calibration_date: string | null;
  calibration_due: string | null;
  zone_id: number | null;
  channels: ValidationSensorChannel[];
}

export interface ValidationZone {
  zone_id: number;
  zone_name: string;
  zone_users: number[];
  sensor_count: number;
}

export interface ValidationUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  isDisabled: boolean;
  created_at: string;
  last_login: string | null;
  roles: string | null;
}

export interface ValidationTelemetry {
  device_serial: string;
  firmware_version: string | null;
  timestamp: string;
  wifi_connected: boolean;
  mqtt_connected: boolean;
  ethernet_connected: boolean;
  connection_type: string | null;
  uptime_hours: number | null;
  total_readings_received: number | null;
  num_sensors_configured: number | null;
  created_at: string;
}

export interface ValidationBackfillStatus {
  sensor_id: number;
  total_entries: number;
  acknowledged: number;
  remaining: number;
  progress_percent: number;
  timeframe_days: number;
  updated_at: string;
}

export interface ValidationAuditLogEntry {
  id: number;
  user_id: number;
  action: string;
  description: string;
  timestamp: string;
}

export interface ValidationAlarmLogEntry {
  idAlarmLog: number;
  activated: string;
  acknowledged: boolean;
  acknowledged_by: string | null;
  sensor_id: number;
  alarm_reset: string | null;
  alarm_type: number;
  active: boolean;
  acknowledged_at: string | null;
  notes: string | null;
  channel: number | null;
}

export interface ValidationData {
  company: string;
  periodFrom: string;
  periodTo: string;
  sensors: ValidationSensor[];
  zones: ValidationZone[];
  users: ValidationUser[];
  telemetry: ValidationTelemetry[];
  backfillStatus: ValidationBackfillStatus[];
  auditLog: ValidationAuditLogEntry[];
  alarmLog: ValidationAlarmLogEntry[];
}

export interface ManualFields {
  siteAddress: string;
  installLocationNotes: string;
  calibrationCertRef: string;
  ambientConditions: string;
  preparedBy: string;
  verifiedBy: string;
  testDate: string;
  testResult: string;
  testNotes: string;
  acceptanceCriteria: string;
  pqPassFail: string;
  pqNotes: string;
}

export const EMPTY_MANUAL_FIELDS: ManualFields = {
  siteAddress: '',
  installLocationNotes: '',
  calibrationCertRef: '',
  ambientConditions: '',
  preparedBy: '',
  verifiedBy: '',
  testDate: '',
  testResult: '',
  testNotes: '',
  acceptanceCriteria: '',
  pqPassFail: '',
  pqNotes: '',
};

// ---------------------------------------------------------------------------
// docx building helpers
// ---------------------------------------------------------------------------

const BLANK = '[TO BE COMPLETED]';
const or = (v: string | number | null | undefined, fallback = BLANK): string =>
  v === null || v === undefined || v === '' ? fallback : String(v);

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString('en-GB');
};

function h1(text: string): Paragraph {
  return new Paragraph({ text, heading: HeadingLevel.HEADING_1 });
}

function h2(text: string): Paragraph {
  return new Paragraph({ text, heading: HeadingLevel.HEADING_2, spacing: { before: 200 } });
}

function body(text: string): Paragraph {
  return new Paragraph({ children: [new TextRun(text)], spacing: { after: 120 } });
}

function cell(text: string, opts: { header?: boolean } = {}): TableCell {
  return new TableCell({
    children: [new Paragraph({ children: [new TextRun({ text, bold: !!opts.header })] })],
    width: { size: 100, type: WidthType.AUTO },
  });
}

function table(headers: string[], rows: string[][]): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({ children: headers.map((hd) => cell(hd, { header: true })) }),
      ...rows.map((r) => new TableRow({ children: r.map((v) => cell(v)) })),
      ...(rows.length === 0
        ? [new TableRow({ children: headers.map(() => cell('No records found')) })]
        : []),
    ],
  });
}

function coverPage(docType: string, docTitle: string, data: ValidationData, refSuffix: string): Paragraph[] {
  const generated = new Date().toLocaleString('en-GB');
  const ref = `TS1-${refSuffix}-${data.company.replace(/\s+/g, '').toUpperCase()}-${new Date()
    .toISOString()
    .slice(0, 10)}`;
  return [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 },
      children: [new TextRun({ text: 'Thermal Systems Ltd', bold: true, size: 28 })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [new TextRun({ text: docTitle, bold: true, size: 40 })],
    }),
    table(
      ['Field', 'Value'],
      [
        ['Document type', docType],
        ['Document reference', ref],
        ['Customer', data.company],
        ['Generated', generated],
      ]
    ),
    new Paragraph({ text: '', spacing: { after: 300 } }),
  ];
}

function signOffSection(manual: ManualFields): Paragraph[] {
  return [
    h2('Sign-off'),
    table(
      ['Role', 'Name', 'Signature', 'Date'],
      [
        ['Prepared by', or(manual.preparedBy), '', ''],
        ['Verified by', or(manual.verifiedBy), '', ''],
        ['Customer acceptance', '', '', ''],
      ]
    ),
  ];
}

async function downloadDocx(doc: Document, filename: string): Promise<void> {
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function filename(company: string, docType: string): string {
  const date = new Date().toISOString().slice(0, 10);
  return `${company.replace(/\s+/g, '_')}_${docType}_${date}.docx`;
}

// ---------------------------------------------------------------------------
// IQ — Installation Qualification
// ---------------------------------------------------------------------------

export function buildIQDocument(data: ValidationData, manual: ManualFields): Document {
  const sensorRows = data.sensors.map((s) => [
    or(s.name),
    or(s.serialNo),
    or(s.type),
    or(s.firmware, '—'),
    fmtDate(s.created_at),
    BLANK, // physical install location — not tracked by the system
  ]);

  const zoneRows = data.zones.map((z) => [
    or(z.zone_name),
    String(z.sensor_count),
    String(z.zone_users?.length ?? 0),
  ]);

  const userRows = data.users.map((u) => [
    `${u.firstName} ${u.lastName}`.trim(),
    u.email,
    or(u.roles, '—'),
    fmtDate(u.created_at),
  ]);

  const telemetryLatestBySerial = new Map<string, ValidationTelemetry>();
  for (const t of data.telemetry) {
    const existing = telemetryLatestBySerial.get(t.device_serial);
    if (!existing || new Date(t.created_at) > new Date(existing.created_at)) {
      telemetryLatestBySerial.set(t.device_serial, t);
    }
  }
  const telemetryRows = Array.from(telemetryLatestBySerial.values()).map((t) => [
    t.device_serial,
    or(t.firmware_version, '—'),
    or(t.connection_type, '—'),
    t.mqtt_connected ? 'Yes' : 'No',
    fmtDate(t.timestamp),
  ]);

  const doc = new Document({
    sections: [
      {
        children: [
          ...coverPage('Installation Qualification (IQ)', 'Installation Qualification', data, 'IQ'),
          h1('1. System Overview'),
          body(
            `This document records the installed configuration of the TS1 monitoring system for ${data.company}, ` +
              'as evidence that the system was installed as specified.'
          ),
          table(
            ['Field', 'Value'],
            [
              ['Customer', data.company],
              ['Site address', or(manual.siteAddress)],
              ['Sensors installed', String(data.sensors.length)],
              ['Zones configured', String(data.zones.length)],
              ['User accounts provisioned', String(data.users.length)],
            ]
          ),
          h1('2. Base Unit / Gateway Connectivity'),
          table(['Device serial', 'Firmware', 'Connection type', 'MQTT connected', 'Last seen'], telemetryRows),
          h1('3. Installed Sensors'),
          table(
            ['Name', 'Serial No.', 'Type', 'Firmware', 'Commissioned (system record)', 'Physical location'],
            sensorRows
          ),
          h1('4. Zone Configuration'),
          table(['Zone', 'Sensor count', 'Assigned users'], zoneRows),
          h1('5. Provisioned User Accounts'),
          table(['Name', 'Email', 'Role ID(s)', 'Account created'], userRows),
          h1('6. Installation Notes'),
          body(or(manual.installLocationNotes, BLANK)),
          ...signOffSection(manual),
        ],
      },
    ],
  });

  return doc;
}

// ---------------------------------------------------------------------------
// OQ — Operational Qualification
// ---------------------------------------------------------------------------

export function buildOQDocument(data: ValidationData, manual: ManualFields): Document {
  const channelRows: string[][] = [];
  for (const s of data.sensors) {
    for (const c of s.channels) {
      channelRows.push([
        or(s.name),
        or(s.serialNo),
        String(c.channel),
        or(c.display_name),
        c.high_enable ? or(c.high_alarm) : 'disabled',
        c.low_enable ? or(c.low_alarm) : 'disabled',
        c.alarms_disabled ? 'Yes' : 'No',
      ]);
    }
  }

  const now = new Date();
  const calibrationRows = data.sensors.map((s) => {
    const due = s.calibration_due ? new Date(s.calibration_due) : null;
    const overdue = due ? due < now : true;
    return [
      or(s.name),
      or(s.serialNo),
      fmtDate(s.calibration_date),
      fmtDate(s.calibration_due),
      overdue ? 'OVERDUE / NOT RECORDED' : 'Within date',
    ];
  });

  const userAccessRows = data.users.map((u) => [
    `${u.firstName} ${u.lastName}`.trim(),
    u.email,
    or(u.roles, '—'),
    u.isDisabled ? 'Disabled' : 'Active',
  ]);

  const auditRows = data.auditLog
    .slice(0, 30)
    .map((a) => [String(a.user_id), a.action, a.description ?? '', fmtDate(a.timestamp)]);

  const telemetryLatestBySerial = new Map<string, ValidationTelemetry>();
  for (const t of data.telemetry) {
    const existing = telemetryLatestBySerial.get(t.device_serial);
    if (!existing || new Date(t.created_at) > new Date(existing.created_at)) {
      telemetryLatestBySerial.set(t.device_serial, t);
    }
  }
  const connectivityRows = Array.from(telemetryLatestBySerial.values()).map((t) => [
    t.device_serial,
    t.wifi_connected ? 'Yes' : 'No',
    t.ethernet_connected ? 'Yes' : 'No',
    t.mqtt_connected ? 'Yes' : 'No',
    or(t.uptime_hours, '—'),
  ]);

  const doc = new Document({
    sections: [
      {
        children: [
          ...coverPage('Operational Qualification (OQ)', 'Operational Qualification', data, 'OQ'),
          h1('1. Purpose'),
          body(
            `This document records evidence that the TS1 monitoring system for ${data.company} operates ` +
              'correctly within its specified operating range: alarm thresholds, calibration status, access ' +
              'control and audit logging.'
          ),
          h1('2. Alarm Threshold Configuration'),
          table(
            ['Sensor', 'Serial No.', 'Channel', 'Name', 'High alarm', 'Low alarm', 'Alarms disabled'],
            channelRows
          ),
          h1('3. Calibration Status'),
          table(['Sensor', 'Serial No.', 'Last calibrated', 'Calibration due', 'Status'], calibrationRows),
          body(`Calibration certificate reference(s): ${or(manual.calibrationCertRef)}`),
          h1('4. User Access Control'),
          table(['Name', 'Email', 'Role ID(s)', 'Account status'], userAccessRows),
          h1('5. Audit Trail Sample'),
          body('Most recent audit log entries, demonstrating that user actions are logged with timestamp.'),
          table(['User ID', 'Action', 'Description', 'Timestamp'], auditRows),
          h1('6. System Connectivity Snapshot'),
          table(['Device serial', 'Wi-Fi', 'Ethernet', 'MQTT', 'Uptime (hrs)'], connectivityRows),
          h1('7. Functional Test'),
          table(
            ['Field', 'Value'],
            [
              ['Test date', or(manual.testDate)],
              ['Test result', or(manual.testResult)],
              ['Notes', or(manual.testNotes)],
            ]
          ),
          ...signOffSection(manual),
        ],
      },
    ],
  });

  return doc;
}

// ---------------------------------------------------------------------------
// PQ — Performance Qualification
// ---------------------------------------------------------------------------

export function buildPQDocument(data: ValidationData, manual: ManualFields): Document {
  const backfillRows = data.backfillStatus.map((b) => [
    String(b.sensor_id),
    String(b.total_entries),
    String(b.remaining),
    `${b.progress_percent}%`,
    fmtDate(b.updated_at),
  ]);

  const uptimeBySerial = new Map<string, { total: number; connected: number }>();
  for (const t of data.telemetry) {
    const entry = uptimeBySerial.get(t.device_serial) ?? { total: 0, connected: 0 };
    entry.total += 1;
    if (t.mqtt_connected) entry.connected += 1;
    uptimeBySerial.set(t.device_serial, entry);
  }
  const uptimeRows = Array.from(uptimeBySerial.entries()).map(([serial, v]) => [
    serial,
    String(v.total),
    v.total > 0 ? `${Math.round((v.connected / v.total) * 100)}%` : '—',
  ]);

  const alarmRows = data.alarmLog
    .slice(0, 30)
    .map((a) => [
      String(a.sensor_id),
      String(a.alarm_type),
      fmtDate(a.activated),
      a.acknowledged ? 'Yes' : 'No',
      a.notes ?? '',
    ]);

  const doc = new Document({
    sections: [
      {
        children: [
          ...coverPage('Performance Qualification (PQ)', 'Performance Qualification', data, 'PQ'),
          h1('1. Purpose'),
          body(
            `This document records evidence that the TS1 monitoring system for ${data.company} performs ` +
              'reliably under normal operating conditions over the qualification period below.'
          ),
          table(
            ['Field', 'Value'],
            [
              ['Qualification period start', fmtDate(data.periodFrom)],
              ['Qualification period end', fmtDate(data.periodTo)],
              ['Acceptance criteria', or(manual.acceptanceCriteria)],
            ]
          ),
          h1('2. Data Completeness'),
          body('Backfill status per sensor as a proxy for data-logging completeness over the period.'),
          table(['Sensor ID', 'Total entries', 'Remaining (gaps)', 'Progress', 'Last updated'], backfillRows),
          h1('3. Connectivity / Uptime Over Period'),
          table(['Device serial', 'Telemetry samples', 'MQTT connected %'], uptimeRows),
          h1('4. Alarm Activity During Period'),
          table(['Sensor ID', 'Alarm type', 'Activated', 'Acknowledged', 'Notes'], alarmRows),
          h1('5. Ambient / Site Conditions'),
          body(or(manual.ambientConditions, BLANK)),
          h1('6. Result'),
          table(
            ['Field', 'Value'],
            [
              ['Pass / Fail', or(manual.pqPassFail)],
              ['Notes', or(manual.pqNotes)],
            ]
          ),
          ...signOffSection(manual),
        ],
      },
    ],
  });

  return doc;
}

export async function generateAndDownload(
  docType: 'IQ' | 'OQ' | 'PQ',
  data: ValidationData,
  manual: ManualFields
): Promise<void> {
  const builder = docType === 'IQ' ? buildIQDocument : docType === 'OQ' ? buildOQDocument : buildPQDocument;
  const doc = builder(data, manual);
  await downloadDocx(doc, filename(data.company, docType));
}
